/**
 * Who owns a lookup key in the client-scoped `RowLocalId.Registry` (one
 * lookup key, one owner).
 *
 * A lookup key is a data address, and every row that carries it reads the
 * same data. Several such rows can exist at once:
 *
 *   - a data READER, e.g. the `data-source` child an alias template expands
 *     (`virtual(r_<alias>|weather)`, `[weather@lona.so]:~weather:{lid}`). Every
 *     sheet that shows the data has its own reader (`:calendar` header,
 *     `:~lona:home` body);
 *   - the ALIAS row that wraps a reader. Its `lookupKey` is the template's
 *     `dataRef` (the parse boundary, `normalizeParseRow`, stamps it), so a
 *     PERSISTED alias (a promoted `:calendar`'s saved
 *     `alias.default-weather`, or a second `alias.weather` for the same
 *     location) reaches `RowNode.decompose` carrying its reader's key.
 *
 * The registry binding exists so the shared access handle
 * (`cellAccessKey(rowId, lookupKey)`) resolves back to row metadata: the
 * fetch path and encoding come from the owner's descriptor. So the owner
 * should be a reader, and it should stay put. Last-write-wins between a
 * persisted alias (bound on every decompose) and its reader (bound on every
 * handle access) moved the key back and forth, and every move tripped
 * `registry-alias/double-claim` (c8 ledger-referee-quiet on a promoted lane:
 * 7 claims between lid5, lid6 and lid43).
 *
 * The rule (`verdict`), for two rows with the same `lookupKey`:
 *
 *   - alias vs reader: the reader owns it. A reader claiming over an alias
 *     takes the key silently (`take`); an alias claiming over a reader joins
 *     it (`join`);
 *   - alias vs alias (e.g. `alias.default-weather` and `alias.weather` for the
 *     same location): wrappers of the same data; `join`;
 *   - two readers of the same type, claimed on handle ACCESS: equivalent
 *     viewers (the round-5 `:calendar` + `:~lona:home` case); the first owner
 *     keeps the key (`join`);
 *   - a real conflict, where `alias` rebinds last-write-wins and the tripwire
 *     fires as before:
 *     - two readers of different types (e.g. a `data-source` and a `text`
 *       row);
 *     - a HYDRATE claim (`RowNode.decompose`, which binds server rows) over
 *       a reader of the same type: two rows with their own ids for one data
 *       key is the duplicate LI8 forbids (decompose's behaviour before this
 *       rule);
 *     - an owner whose metadata is missing or carries another key.
 */
import type { Option } from "@tento-lona/sheets";
import { RowKey, type RowLocalId } from "@tento-lona/sheets";

export namespace LookupKeyOwner {
  /** What `verdict` needs to know about a claimant or the current owner. */
  export interface Claimant {
    readonly type: string;
    readonly lookupKey: Option<string>;
  }

  /**
   * Where the claim comes from: `hydrate` (`RowNode.decompose`, server rows)
   * or `access` (`CellAccessor._accessKeyFor`, any row a handle is made for).
   */
  export type Source = "hydrate" | "access";

  /**
   * The rows `claim` reads a key's current owner from: its wire type and
   * lookup key (`SheetRow.Registry` is one). Spelled here so the ownership
   * rule needs nothing of the row model.
   */
  export interface Rows {
    get(id: RowLocalId): Option<{ readonly raw: { readonly type: string }; readonly lookupKey: Option<string> }>;
  }

  /**
   * Alias wire types after parse normalization: `alias.<template>` for
   * catalog templates, generic `alias` (+ `attributes.template`) otherwise,
   * and the tolerated legacy bare `weather`.
   */
  export function isAlias(type: string): boolean {
    return type === "alias" || type.startsWith("alias.") || type === "weather";
  }

  export function verdict(
    owner: Option<Claimant>,
    incoming: Claimant,
    source: Source,
  ): RowLocalId.Claim {
    if (owner == null || owner.lookupKey !== incoming.lookupKey) {
      return "conflict";
    }
    const ownerAlias = isAlias(owner.type);
    const incomingAlias = isAlias(incoming.type);
    if (ownerAlias && incomingAlias) return "join";
    if (ownerAlias) return "take";
    if (incomingAlias) return "join";
    if (owner.type === incoming.type && source === "access") return "join";
    return "conflict";
  }

  /**
   * Bind `lookupKey` for the row `localId` of wire type `type` under the rule
   * above. Returns the key's owner after the call.
   */
  export function claim(
    ids: RowLocalId.Registry,
    rows: LookupKeyOwner.Rows,
    lookupKey: string,
    localId: RowLocalId,
    type: string,
    source: Source,
  ): RowLocalId {
    return ids.settle(RowKey.lookup(lookupKey), localId, (owner) => {
      const ownerRow = rows.get(owner);
      return verdict(
        ownerRow == null
          ? null
          : { type: ownerRow.raw.type, lookupKey: ownerRow.lookupKey },
        { type, lookupKey },
        source,
      );
    });
  }
}
